import React, { useState, useMemo, useEffect } from 'react';
import {
    Box, Paper, Typography, Stack, Select, MenuItem, FormControl,
    InputLabel, Button, CircularProgress, Grid, InputAdornment,
    Divider, useTheme, Link, Radio, RadioGroup, FormControlLabel
} from '@mui/material';
import {
    Visibility as VisibilityIcon,
    CalendarMonth as CalendarIcon,
    AccessTime as TimeIcon,
    FilterAlt as FilterIcon,
    BarChart as BarChartIcon,
    Badge as BadgeIcon
} from '@mui/icons-material';
import { Investigador, VersionSelection } from './types/rankingTypes';
import ConsultasService from './services/ConsultasService';

interface VersionSelectorProps {
    rawdata: Investigador[];
    onShowRanking: (selection: VersionSelection) => void;
    loading: boolean;
    setCargo: (cargo: string) => void;
    setTipoReporte: (tipoReporte: string) => void;
    setId: (id: number) => void;
    onBack?: () => void;
}

interface FechaOption {
    fecha: string;
    id: number;
    isOficial: boolean;
    tipocargo: string;
    conautocitas: string;
    estado: boolean;
}

interface FechaUnidadesOption {
    iddecinvrankingunidadperiodo: number,
    idpersona?: number,
    anio: number,
    fwciref?: string,
    horassinvref?: string,
    publicacionesre?: string,
    estado?: string,
    esoficial?: string;
    pubicar?: string,
    fechacreacion: string
}

export const VersionSelector: React.FC<VersionSelectorProps> = ({ rawdata, setId, onShowRanking, loading, onBack, setCargo, setTipoReporte }) => {
    const theme = useTheme();
    const [selectedAnio, setSelectedAnio] = useState<string>('');
    const [selectedFecha, setSelectedFecha] = useState<string>('');
    const [selectedAutocitas, setSelectedAutocitas] = useState<string>('');
    const [selectedCargo, setSelectedCargo] = useState<string>('');
    const [tipo, setTipo] = useState('investigadores');
    const [fechaOptions, setFechaOptions] = useState<FechaOption[]>([]);
    const [fechaUnidadesOptions, setFechaUnidadesOptions] = useState<FechaUnidadesOption[]>([]);
    const [cargandoFechas, setCargandoFechas] = useState(false);
    const cargoOptions = ['', 'Profesor', 'Técnico de investigación'];
    const [selectedId, setSelectedId] = useState<number | string>('');
    const datosInvestigador = !selectedAnio || !selectedFecha || !selectedAutocitas;
    const datosUnidades = !selectedAnio || !selectedFecha;
    const formatearFecha = (fechaOriginal: string) => {
        if (!fechaOriginal) return '';
        const date = new Date(fechaOriginal);
        if (isNaN(date.getTime())) return fechaOriginal;
        const aaaa = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        const hh = String(date.getHours()).padStart(2, '0');
        const min = String(date.getMinutes()).padStart(2, '0');
        const ss = String(date.getSeconds()).padStart(2, '0');
        return `${aaaa}-${mm}-${dd} ${hh}:${min}:${ss}`;
    };

    const selectTipoReporte = (e: any) => {
        setTipo(e.target.value);
        setSelectedAnio("");
        setSelectedFecha("");
        setSelectedId("");
        setSelectedAutocitas("");
        setSelectedCargo("");
    };

    // --- CONSUMO DEL SERVICIO CUANDO CAMBIA EL AÑO ---
    useEffect(() => {
        const obtenerFechas = async () => {
            if (!selectedAnio) {
                setFechaOptions([]);
                return;
            }
            setCargandoFechas(true);
            try {
                const dateParam = new Date(parseInt(selectedAnio), 0, 1);
                if (tipo == 'investigadores') {
                    const response = await ConsultasService.GetFechasCreacionByYear(2026);
                    const dataSegura = response?.listData || [];

                    const opcionesMapeadas: FechaOption[] = dataSegura
                        .filter((item: any) => item.estado === "C")
                        .map((item: any) => ({
                            fecha: item.fechacreacion || item,
                            id: item.iddecinvrankinginvestigadorperiodo,
                            isOficial: item.esoficial === 'S' || item.versionoficial === 'S',
                            tipocargo: item.tipocargo,
                            conautocitas: item.conautocitas
                        }));
                    opcionesMapeadas.sort((a, b) => b.fecha.localeCompare(a.fecha));
                    setFechaOptions(opcionesMapeadas);
                } else if (tipo == "centros") {
                    const response = await ConsultasService.GetCentrosUnidadesFechasCreacionByYear(2026);
                    const fechasFiltradas=response?.listData.filter((item: any) => item.estado === "C")
                    setFechaUnidadesOptions(fechasFiltradas || []);
                }

            } catch (error) {
                console.error("Error al obtener las fechas del API:", error);
                setFechaOptions([]);
            } finally {
                setCargandoFechas(false);
            }
        };

        obtenerFechas();
    }, [selectedAnio, tipo]);

    const handleButtonClick = () => {
        setCargo(selectedCargo);
        setTipoReporte(tipo);

        let flagOficial = false;  

        if (tipo === 'investigadores') {
            const opcionSeleccionada = fechaOptions.find(opt => opt.id === selectedId);
            if (opcionSeleccionada) {
                setId(opcionSeleccionada.id);
                flagOficial = opcionSeleccionada.isOficial;  
            }
        } else if (tipo === 'centros') {
            const opcionSeleccionada = fechaUnidadesOptions.find(opt => opt.iddecinvrankingunidadperiodo === selectedId);
            if (opcionSeleccionada) {
                setId(opcionSeleccionada.iddecinvrankingunidadperiodo);
                flagOficial = opcionSeleccionada.esoficial === 'S';  
            }
        }

        onShowRanking({
            anio: selectedAnio,
            fecha: selectedFecha,
            autocitas: selectedAutocitas,
            cargo: selectedCargo,
            isOficial: flagOficial 
        });
    };
    return (
        <Box sx={{ p: { xs: 2, md: 2 }, minHeight: '80vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.50' }}>

            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <BarChartIcon sx={{ fontSize: 40, color: '#243165' }} />
                <Typography variant="h4" fontWeight="bold" sx={{ color: '#243165' }}>
                    Ranking
                </Typography>
            </Stack>

            <Paper elevation={4} sx={{ p: { xs: 3, md: 5 }, borderRadius: '24px', maxWidth: '700px', width: '100%', borderTop: '6px solid #243165' }}>
                <Typography variant="h5" sx={{ mb: 1, fontWeight: 'bold', color: 'text.primary' }}>
                    Configuración del Reporte
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Seleccione los parámetros de la versión histórica que desea consultar.
                </Typography>

                {/* Grupo de Radios para Investigadores y Centros */}
                <FormControl component="fieldset" sx={{ mb: 2, width: '100%', display: 'flex', alignItems: 'center' }}>
                    <RadioGroup
                        row
                        aria-label="tipo-reporte"
                        name="tipo-reporte"
                        value={tipo}
                        onChange={(e) => selectTipoReporte(e)}
                    >
                        <FormControlLabel
                            value="investigadores"
                            control={<Radio sx={{ color: '#243165', '&.Mui-checked': { color: '#243165' } }} />}
                            label="Investigadores"
                            sx={{ mr: 4 }}
                        />
                        <FormControlLabel
                            value="centros"
                            control={<Radio sx={{ color: '#243165', '&.Mui-checked': { color: '#243165' } }} />}
                            label="Centros/Unidades"
                        />
                    </RadioGroup>
                </FormControl>

                <Divider sx={{ mb: 4 }} />

                <Grid container spacing={3}>
                    {/* Campo Año */}
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth variant="outlined">
                            <InputLabel id="anio-label">Año</InputLabel>
                            <Select
                                labelId="anio-label"
                                value={selectedAnio}
                                label="Año"
                                startAdornment={<InputAdornment position="start"><CalendarIcon color="action" /></InputAdornment>}
                                onChange={(e) => {
                                    setSelectedAnio(e.target.value);
                                    setSelectedFecha('');
                                    setSelectedAutocitas('');
                                    setSelectedCargo('');
                                }}
                            >
                                <MenuItem key={"2026"} value={"2026"}>{"2026"}</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    {tipo == 'investigadores' && (
                        <>
                            {/* Campo Fecha */}
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth disabled={!selectedAnio || cargandoFechas}>
                                    <InputLabel id="fecha-label">
                                        {cargandoFechas ? 'Cargando fechas...' : 'Fecha de Creación'}
                                    </InputLabel>
                                    <Select
                                        labelId="fecha-label"
                                        value={selectedId} // <--- AHORA EL SELECT USA EL ID ÚNICO
                                        label={cargandoFechas ? 'Cargando fechas...' : 'Fecha de Creación'}
                                        startAdornment={
                                            <InputAdornment position="start">
                                                {cargandoFechas
                                                    ? <CircularProgress size={20} />
                                                    : <TimeIcon color={!selectedAnio ? 'disabled' : 'action'} />
                                                }
                                            </InputAdornment>
                                        }
                                        onChange={(e) => {
                                            const idSeleccionado = e.target.value;
                                            setSelectedId(idSeleccionado); // Guardamos el ID

                                            // Buscamos la opción seleccionada usando el ID, no la fecha
                                            const opcionSeleccionada = fechaOptions.find(opt => opt.id === idSeleccionado);

                                            if (opcionSeleccionada) {
                                                setSelectedFecha(opcionSeleccionada.fecha); // Guardamos la fecha para mantener tu lógica anterior

                                                if (opcionSeleccionada.tipocargo === 'P') {
                                                    setSelectedCargo('Profesor');
                                                } else if (opcionSeleccionada.tipocargo === 'T') {
                                                    setSelectedCargo('Técnico de investigación');
                                                } else {
                                                    setSelectedCargo('');
                                                }
                                                setSelectedAutocitas(opcionSeleccionada.conautocitas);
                                            }
                                        }}
                                        // <--- RENDER VALUE: Busca la fecha correspondiente al ID para mostrarla en el input
                                        renderValue={(id) => {
                                            const opt = fechaOptions.find(o => o.id === id);
                                            return opt ? formatearFecha(opt.fecha) : '';
                                        }}
                                    >
                                        {fechaOptions.length === 0 && !cargandoFechas && selectedAnio && (
                                            <MenuItem value="" disabled>No se encontraron fechas</MenuItem>
                                        )}

                                        {fechaOptions.map((option) => (
                                            // <--- EL VALUE DEL MENU ITEM AHORA ES EL ID
                                            <MenuItem key={`${option.fecha}-${option.id}`} value={option.id}>

                                                {formatearFecha(option.fecha)}

                                                {option.isOficial && (
                                                    <Typography component="span" variant="body2" color="primary.main" sx={{ fontWeight: 'bold', ml: 1 }}>
                                                        - Corte Final
                                                    </Typography>
                                                )}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </>
                    )}

                    {tipo == 'centros' && (
                        <>
                            {/* Campo Fecha */}
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth disabled={!selectedAnio || cargandoFechas}>
                                    <InputLabel id="fecha-label">
                                        {cargandoFechas ? 'Cargando fechas...' : 'Fecha de Creación'}
                                    </InputLabel>
                                    <Select
                                        labelId="fecha-label"
                                        value={selectedId} // <--- AHORA EL SELECT USA EL ID ÚNICO
                                        label={cargandoFechas ? 'Cargando fechas...' : 'Fecha de Creación'}
                                        startAdornment={
                                            <InputAdornment position="start">
                                                {cargandoFechas
                                                    ? <CircularProgress size={20} />
                                                    : <TimeIcon color={!selectedAnio ? 'disabled' : 'action'} />
                                                }
                                            </InputAdornment>
                                        }
                                        onChange={(e) => {
                                            const idSeleccionado = e.target.value;
                                            setSelectedId(idSeleccionado); // Guardamos el ID

                                            // Buscamos la opción seleccionada usando el ID, no la fecha
                                            const opcionSeleccionada = fechaUnidadesOptions.find(opt => opt.iddecinvrankingunidadperiodo === idSeleccionado);

                                            if (opcionSeleccionada) {
                                                setSelectedFecha(opcionSeleccionada.fechacreacion); // Guardamos la fecha para mantener tu lógica anterior
                                            }
                                        }}
                                        // <--- RENDER VALUE: Busca la fecha correspondiente al ID para mostrarla en el input
                                        renderValue={(id) => {
                                            const opt = fechaUnidadesOptions.find(o => o.iddecinvrankingunidadperiodo === id);
                                            return opt ? formatearFecha(opt.fechacreacion) : '';
                                        }}
                                    >
                                        {fechaUnidadesOptions.length === 0 && !cargandoFechas && selectedAnio && (
                                            <MenuItem value="" disabled>No se encontraron fechas</MenuItem>
                                        )}

                                        {fechaUnidadesOptions.map((option) => (
                                            // <--- EL VALUE DEL MENU ITEM AHORA ES EL ID
                                            <MenuItem key={`${option.fechacreacion}-${option.iddecinvrankingunidadperiodo}`} value={option.iddecinvrankingunidadperiodo}>
                                                {formatearFecha(option.fechacreacion)}
                                                {option.esoficial === 'S' && (
                                                    <Typography component="span" variant="body2" color="primary.main" sx={{ fontWeight: 'bold', ml: 1 }}>
                                                        - Corte Final
                                                    </Typography>
                                                )}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </>
                    )}
                    {tipo == 'investigadores' && (
                        <>
                            {/* Campo Cargo/Tipo */}
                            < Grid item xs={12} md={6}>
                                <FormControl fullWidth disabled={true}>
                                    <InputLabel id="cargo-label">Cargo</InputLabel>
                                    <Select
                                        labelId="cargo-label"
                                        value={selectedCargo}
                                        label="Cargo"
                                        startAdornment={<InputAdornment position="start"><BadgeIcon color="action" /></InputAdornment>}
                                        onChange={(e) => setSelectedCargo(e.target.value)}
                                    >tipo
                                        {cargoOptions.map(op => <MenuItem key={op} value={op}>{op === '' ? 'Todos' : op}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Campo Autocitas */}
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth disabled={true}>
                                    <InputLabel id="autocitas-label">Filtro de Autocitas</InputLabel>
                                    <Select
                                        labelId="autocitas-label"
                                        value={selectedAutocitas}
                                        label="Filtro de Autocitas"
                                        startAdornment={<InputAdornment position="start"><FilterIcon color={!selectedFecha ? 'disabled' : 'action'} /></InputAdornment>}
                                        onChange={(e) => setSelectedAutocitas(e.target.value)}
                                    >
                                        <MenuItem value="S">{"Incluir Autocitas (Sí)"}</MenuItem>
                                        <MenuItem value="N">{"Excluir Autocitas (No)"}</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </>
                    )}
                    {/* Botón de Acción */}
                    <Grid item xs={12} sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Button
                            variant="contained" size="large" fullWidth
                            disabled={(tipo === 'investigadores' ? datosInvestigador : datosUnidades) || loading || cargandoFechas}
                            onClick={handleButtonClick}
                            sx={{
                                py: 1.8, fontSize: '1.1rem', fontWeight: 'bold', borderRadius: '12px', textTransform: 'none', backgroundColor: '#243165', color: '#fff !important',
                                '&:hover': { backgroundColor: '#1a234a' },
                                boxShadow: '0 4px 12px rgba(36, 49, 101, 0.3)',
                                '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' }
                            }}
                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <VisibilityIcon />}
                        >
                            {loading ? 'Cargando datos...' : 'Visualizar Ranking'}
                        </Button>

                        {/* Link de Volver */}
                        {onBack && (
                            <Link component="button" variant="body2" onClick={onBack} sx={{ mt: 2, color: '#243165', textDecoration: 'underline', fontWeight: 'medium', '&:hover': { color: '#1a234a' } }}>
                                Volver
                            </Link>
                        )}
                    </Grid>
                </Grid>
            </Paper>
        </Box >
    );
};